// How an apartment's status reads everywhere (docs/product-ux 08 §1): the same Hebrew terms,
// icons and tones in the navigator and the apartment workspace. Three dimensions, always apart:
// handover stage, defects, exceptions. Each always pairs its color with text and an icon. There
// is no "delivered" until a signing process exists.
import type { ApartmentException, HandoverStage } from '@sivan/shared';
import type { IconName } from '../ui/Icon';

export type Tone = 'neutral' | 'info' | 'final' | 'ok' | 'warn' | 'danger';

export interface StatusMeta {
  /** The full label (lists, details, accessible names). */
  readonly label: string;
  /** The short label of a tile. */
  readonly short: string;
  readonly icon: IconName;
  readonly tone: Tone;
}

export const STAGE_META: Readonly<Record<HandoverStage, StatusMeta>> = {
  unavailable: { label: 'מצב המסירה לא זמין', short: 'לא זמין', icon: 'lock', tone: 'neutral' },
  not_started: { label: 'טרם החלה', short: 'טרם החלה', icon: 'circle', tone: 'neutral' },
  scheduled: {
    label: 'מסירה מתוכננת',
    short: 'מתוכננת',
    icon: 'calendar',
    tone: 'info',
  },
  initial_in_progress: {
    label: 'מסירה ראשונית בתהליך',
    short: 'ראשונית: בתהליך',
    icon: 'pencil',
    tone: 'info',
  },
  initial_summary: {
    label: 'ראשונית סוכמה (לא חתומה)',
    short: 'ראשונית: סוכמה',
    icon: 'circle-check',
    tone: 'info',
  },
  final_in_progress: {
    label: 'מסירה סופית בתהליך',
    short: 'סופית: בתהליך',
    icon: 'pencil',
    tone: 'final',
  },
  final_summary: {
    label: 'סופית סוכמה (לא חתומה)',
    short: 'סופית: סוכמה',
    icon: 'circle-check',
    tone: 'final',
  },
};

export const EXCEPTION_META: Readonly<Record<ApartmentException, StatusMeta>> = {
  missing_model_type: {
    label: 'חסר טיפוס דירה',
    short: 'חסר טיפוס',
    icon: 'alert',
    tone: 'warn',
  },
  missing_floor: { label: 'חסרה קומה', short: 'חסרה קומה', icon: 'alert', tone: 'warn' },
  inconsistent_state: {
    label: 'מצב לא עקבי — לבדיקה',
    short: 'לא עקבי',
    icon: 'alert',
    tone: 'warn',
  },
};

/** The defects dimension (08 §1.2); null when handovers are not visible to the reader. */
export function defectMeta(
  defects: { readonly open: number; readonly awaiting: number } | null,
): StatusMeta | null {
  if (defects === null) return null;
  if (defects.open > 0)
    return {
      label: defects.open === 1 ? 'ליקוי פתוח אחד' : `${String(defects.open)} ליקויים פתוחים`,
      short: String(defects.open),
      icon: 'alert',
      tone: 'danger',
    };
  if (defects.awaiting > 0)
    return {
      label: `${String(defects.awaiting)} ממתינים לבדיקה חוזרת`,
      short: String(defects.awaiting),
      icon: 'clock',
      tone: 'warn',
    };
  return { label: 'אין ליקויים פתוחים', short: '', icon: 'check', tone: 'neutral' };
}
